import React, { useState } from "react";
import { Coins, Trophy, History as HistoryIcon, Sparkles, Lock } from "lucide-react";
import { useTokens } from "./TokenContext";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";

export default function TokenWallet() {
  const { user } = useAuth();
  const { t } = useT();
  const {
    balance, earned, spent, total_points, progress_pct, next_token_at,
    points_per_token, history, loading, simulateWin,
  } = useTokens();
  const isAdmin = user?.role === "admin";
  const [busy, setBusy] = useState(false);

  const onSimulate = async () => {
    setBusy(true);
    try { await simulateWin(); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="v2-token-wallet" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Coins className="w-4 h-4 text-[#FCD34D]" /> {t("v2.tok.wallet")}
        </h3>
        <span className="text-[9px] font-mono uppercase tracking-wider text-[#71717A]">{t("v2.tok.ratio", { n: points_per_token })}</span>
      </div>

      <div className="relative overflow-hidden rounded-3xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#1A1408] via-[#0F0902] to-[#0A0A0F] p-5 shadow-xl shadow-[#F59E0B]/10">
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#F59E0B]/15 blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-4">
          <CoinBadge size={70} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FCD34D]/80">{t("v2.tok.balance")}</div>
            <div className="flex items-baseline gap-2">
              <span data-testid="v2-wallet-balance" className="text-4xl font-bold text-[#FCD34D] tabular-nums leading-none">{loading ? "—" : balance}</span>
              <span className="text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">{t("v2.tok.tokens")}</span>
            </div>
            <div className="text-[11px] text-[#71717A] mt-1">{t("v2.tok.earned", { e: earned, s: spent })}</div>
          </div>
        </div>
        <div className="relative mt-5">
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA] mb-1">
            <span>{t("v2.tok.next")}</span>
            <span className="text-[#FCD34D]">{total_points} / {next_token_at} {t("v2.tok.pts")}</span>
          </div>
          <div className="h-2 bg-[#27272F] rounded-full overflow-hidden">
            <div
              data-testid="v2-wallet-progress"
              className="h-full bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#FCD34D] transition-all duration-500"
              style={{ width: `${Math.min(100, progress_pct)}%` }}
            />
          </div>
          <div className="text-[10px] text-[#71717A] mt-2 italic flex items-center gap-1">
            <Lock className="w-3 h-3" /> {t("v2.tok.unlock")}
          </div>
        </div>
      </div>

      {isAdmin && (
        <button
          type="button"
          onClick={onSimulate}
          disabled={busy}
          data-testid="v2-wallet-simulate-win"
          className="w-full flex items-center justify-center gap-2 rounded-2xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 disabled:opacity-50 px-4 py-3 transition-colors"
        >
          <Sparkles className="w-4 h-4 text-[#FCD34D]" />
          <span className="text-xs font-bold text-[#FCD34D] uppercase tracking-wider">{busy ? t("v2.tok.simulating") : t("v2.tok.simulate")}</span>
        </button>
      )}

      <div data-testid="v2-wallet-history">
        <div className="flex items-center gap-2 mb-2">
          <HistoryIcon className="w-3.5 h-3.5 text-[#71717A]" />
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA]">{t("v2.tok.activity")}</h4>
        </div>
        {history.length === 0 ? (
          <div className="bg-[#12121C] border border-dashed border-[#1F1F2A] rounded-2xl p-5 text-center text-xs text-[#71717A]">
            {t("v2.tok.empty")}
          </div>
        ) : (
          <div className="space-y-1.5">
            {history.slice(0, 12).map((row, i) => (
              <HistoryRow key={`${row.match_id || row.at || ""}-${i}`} row={row} t={t} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function HistoryRow({ row, t }) {
  const isSim = row.type === "simulated";
  const win = row.outcome === "win" || isSim;
  const draw = row.outcome === "draw";
  const ratio = 5; // matches TOKENS_POINTS_PER_TOKEN
  const tokensCrossed = (row.career_points_after && row.career_points_before != null)
    ? Math.floor(row.career_points_after / ratio) - Math.floor(row.career_points_before / ratio)
    : 0;

  let label, color, bg, shortLabel;
  if (isSim) { label = t("v2.tok.simulated"); shortLabel = "SIM"; color = "#F59E0B"; bg = "rgba(245,158,11,0.15)"; }
  else if (win) { label = t("v2.tok.outcomeWin"); shortLabel = "W"; color = "#10B981"; bg = "rgba(16,185,129,0.15)"; }
  else if (draw) { label = t("v2.tok.outcomeDraw"); shortLabel = "D"; color = "#F59E0B"; bg = "rgba(245,158,11,0.15)"; }
  else { label = t("v2.tok.outcomeLoss"); shortLabel = "L"; color = "#EF4444"; bg = "rgba(239,68,68,0.15)"; }

  return (
    <div data-testid={`v2-wallet-row-${row.match_id || row.at}`} className="flex items-center gap-3 bg-[#12121C] border border-[#1F1F2A] rounded-2xl px-3 py-2.5">
      <span className="w-9 h-9 rounded-xl flex items-center justify-center text-[10px] font-bold uppercase tracking-wider flex-shrink-0" style={{ backgroundColor: bg, color }}>
        {shortLabel}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white truncate">
          {isSim ? t("v2.tok.simWin") : (
            <>
              {label} {row.opponent_username ? `· ${row.opponent_username}` : ""}
              {row.score && <span className="ml-1.5 text-[10px] text-[#71717A] font-mono">{row.score}</span>}
            </>
          )}
        </div>
        <div className="text-[10px] text-[#71717A] mt-0.5">
          +{row.points} {t("v2.tok.pts")} · {t("v2.tok.runningTotal", { pts: row.career_points_after })}
          {tokensCrossed > 0 && <span className="ml-1.5 text-[#FCD34D] font-bold">· {t("v2.tok.tokenGained", { n: tokensCrossed })}</span>}
        </div>
      </div>
      <div className="text-right text-[10px] font-mono text-[#52525B] flex-shrink-0">
        {row.at ? new Date(row.at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
      </div>
    </div>
  );
}

function CoinBadge({ size = 64 }) {
  const px = `${size}px`;
  return (
    <div
      className="relative flex-shrink-0 rounded-full bg-gradient-to-br from-[#FFD700] via-[#F59E0B] to-[#B45309] shadow-[0_0_30px_rgba(245,158,11,0.4)] ring-2 ring-[#FCD34D]/40"
      style={{ width: px, height: px }}
    >
      <div className="absolute inset-1.5 rounded-full bg-gradient-to-br from-[#FCD34D] via-[#F59E0B] to-[#92400E] flex items-center justify-center">
        <Coins className="w-1/2 h-1/2 text-[#3B2503]" strokeWidth={2.5} />
      </div>
      {/* Specular highlight */}
      <div className="absolute top-2 left-2 w-3 h-3 rounded-full bg-white/60 blur-[2px]" />
    </div>
  );
}
